import { request } from '@/lib/api'

/**
 * Разбор обращений ИИ (#132): правила маршрутизации (администрирование, `system.settings`) и
 * сводка по обращениям за период (`support.ticket:read` в области смотрящего).
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе.
 */

export type AssignMode = 'user' | 'department_head' | 'keep'

export interface RoutingRule {
  id: string
  name: string
  priority: number
  active: boolean
  categories?: string[]
  tones?: string[]
  urgencies?: string[]
  assignMode: AssignMode
  assignUserId?: string | null
  assignUserName?: string | null
  markUrgent: boolean
  version: number
}

export interface RoutingCandidate {
  id: string
  name: string
}

export interface RoutingSetup {
  rules: RoutingRule[]
  /** Справочники сервера — экран не держит своей копии кодов. */
  categories: string[]
  tones: string[]
  urgencies: string[]
  candidates: RoutingCandidate[]
}

export interface RoutingRuleInput {
  name?: string
  priority?: number
  active?: boolean
  categories?: string[]
  tones?: string[]
  urgencies?: string[]
  assignMode?: AssignMode
  assignUserId?: string | null
  markUrgent?: boolean
}

export function fetchRouting(signal?: AbortSignal): Promise<RoutingSetup> {
  return request<Partial<RoutingSetup>>('/admin/support-routing-rules', { signal }).then((body) => ({
    rules: body.rules ?? [],
    categories: body.categories ?? [],
    tones: body.tones ?? [],
    urgencies: body.urgencies ?? [],
    candidates: body.candidates ?? [],
  }))
}

export function createRoutingRule(body: RoutingRuleInput): Promise<RoutingRule> {
  return request<RoutingRule>('/admin/support-routing-rules', { method: 'POST', body })
}

export function updateRoutingRule(id: string, body: RoutingRuleInput): Promise<RoutingRule> {
  return request<RoutingRule>(`/admin/support-routing-rules/${id}`, { method: 'PUT', body })
}

export function deleteRoutingRule(id: string): Promise<void> {
  return request<void>(`/admin/support-routing-rules/${id}`, { method: 'DELETE' })
}

export interface InsightLine {
  category: string
  count: number
  previous: number
  share: number
  harsh: number
  urgent: number
  /** Во сколько раз больше прошлого периода; нет — в прошлом периоде не было ни одного. */
  change?: number | null
  spike: boolean
}

export interface Insights {
  from: string
  to: string
  previousFrom: string
  previousTo: string
  total: number
  previousTotal: number
  unread: number
  urgent: number
  tones?: Record<string, number>
  categories?: InsightLine[]
  spikes?: string[]
  spikeMinCount: number
  spikeRatio: number
}

export function fetchInsights(from: string | null, to: string | null, signal?: AbortSignal): Promise<Insights> {
  const query = new URLSearchParams()
  if (from) query.set('from', from)
  if (to) query.set('to', to)
  const suffix = query.toString()
  return request<Insights>(`/support/insights${suffix ? `?${suffix}` : ''}`, { signal })
}
